// modules/clientes/components/ServicioCambiarClienteDialog.jsx
import React, { useState, useEffect } from "react";
import {
  Box, Alert, Grid, Autocomplete, TextField, CircularProgress, Button,
  Dialog, DialogTitle, DialogContent, DialogActions, Divider,
} from "@mui/material";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import * as clienteService from "../services/clienteService";

const DIALOG_COMPACT_SX = {
  pt: 1,
  "& .MuiTextField-root .MuiInputBase-root:not(.MuiInputBase-multiline)": {
    minHeight: 36,
  },
  "& .MuiInputBase-input": {
    fontSize: "0.82rem",
  },
  "& .MuiInputBase-input:not(textarea)": {
    py: "7px",
  },
  "& .MuiSelect-select": {
    py: "7px",
  },
  "& .MuiInputLabel-root": {
    fontSize: "0.82rem",
  },
};

const ServicioCambiarClienteDialog = ({ open, clienteActual, servicio, onClose, onMoved }) => {
  const [buscar, setBuscar] = useState("");
  const [clientesDestino, setClientesDestino] = useState([]);
  const [clienteDestino, setClienteDestino] = useState(null);
  const [observacion, setObservacion] = useState("");
  const [loadingSearch, setLoadingSearch] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    setBuscar("");
    setClientesDestino([]);
    setClienteDestino(null);
    setObservacion("");
    setError(null);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const text = buscar.trim();
    if (text.length < 2) {
      setClientesDestino([]);
      return;
    }

    let active = true;
    const timer = setTimeout(async () => {
      setLoadingSearch(true);
      try {
        const { data } = await clienteService.getAll({ buscar: text, page: 1, pageSize: 10 });
        const payload = data?.data || data || {};
        const rows = Array.isArray(payload.rows) ? payload.rows : [];
        const filtrados = rows.filter((item) => item.cod_cliente !== clienteActual.cod_cliente);
        if (active) setClientesDestino(filtrados);
      } catch {
        if (active) setClientesDestino([]);
      } finally {
        if (active) setLoadingSearch(false);
      }
    }, 350);

    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [open, buscar, clienteActual.cod_cliente]);

  const handleSave = async () => {
    if (!clienteDestino?.cod_cliente) {
      setError("Debe seleccionar el cliente destino");
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const { data } = await clienteService.cambiarClienteServicio(
        clienteActual.cod_cliente,
        servicio.cod_servicio_cliente,
        {
          cod_cliente_destino: clienteDestino.cod_cliente,
          observacion: observacion.trim(),
        },
      );
      onMoved(data?.data || data || { ...servicio, cod_cliente: clienteDestino.cod_cliente });
    } catch (err) {
      setError(err?.response?.data?.message || "No se pudo cambiar el cliente del servicio");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Cambiar cliente del servicio #{servicio.cod_servicio_cliente}</DialogTitle>
      <Divider />
      <DialogContent>
        <Box sx={DIALOG_COMPACT_SX}>
          {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}
          <Grid container spacing={1.25}>
            <Grid size={{ xs: 12 }}>
              <Alert severity="warning" sx={{ py: 0.75 }}>
                El servicio saldra de <strong>{clienteActual.razon_social}</strong> y quedara vinculado al cliente destino.
              </Alert>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Autocomplete
                size="small"
                options={clientesDestino}
                loading={loadingSearch}
                value={clienteDestino}
                inputValue={buscar}
                onInputChange={(_, value) => setBuscar(value)}
                onChange={(_, value) => setClienteDestino(value)}
                getOptionLabel={(option) =>
                  option?.cod_cliente ? `${option.cod_cliente} - ${option.razon_social || ""}` : ""
                }
                isOptionEqualToValue={(option, value) => option.cod_cliente === value.cod_cliente}
                noOptionsText={buscar.trim().length < 2 ? "Escriba al menos 2 caracteres" : "Sin clientes"}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Cliente destino"
                    placeholder="Buscar por codigo o razon social"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingSearch && <CircularProgress size={14} />}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                size="small"
                multiline
                rows={2}
                label="Observacion"
                value={observacion}
                onChange={(e) => setObservacion(e.target.value)}
                disabled={saving}
              />
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <Divider />
      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Button onClick={onClose} disabled={saving}>Cancelar</Button>
        <Button
          variant="contained"
          color="warning"
          onClick={handleSave}
          disabled={saving || !clienteDestino?.cod_cliente}
          startIcon={saving ? <CircularProgress size={14} /> : <SwapHorizIcon />}
        >
          {saving ? "Guardando..." : "Cambiar cliente"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ServicioCambiarClienteDialog;
